import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { NewsletterForm } from "@/components/newsletter/NewsletterForm";
import { useAuth } from "@/contexts/AuthContext";

export const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isHomePage = location.pathname === "/";
  const { user, isSubscriber } = useAuth();
  const showPartnersLink = user && isSubscriber;
  const [logos, setLogos] = useState({
    logo: "",
    logoDark: "",
    logoResponsive: "",
    logoResponsiveDark: ""
  });
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const loadLogos = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'platform_branding')
        .maybeSingle();

      if (data?.value) {
        const branding = data.value as {
          logo?: string;
          logoDark?: string;
          logoResponsive?: string;
          logoResponsiveDark?: string;
        };
        setLogos({
          logo: branding.logo || "",
          logoDark: branding.logoDark || "",
          logoResponsive: branding.logoResponsive || "",
          logoResponsiveDark: branding.logoResponsiveDark || ""
        });
      }
    };

    loadLogos();
  }, []);

  return (
    <footer className="bg-dark text-white border-t-[6px] border-primary">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Column 1 - About */}
          <div>
            <div className="mb-4">
              {(isMobile ? (logos.logoResponsiveDark || logos.logoResponsive) : (logos.logoDark || logos.logo)) ? (
                <img
                  src={isMobile ? (logos.logoResponsiveDark || logos.logoResponsive) : (logos.logoDark || logos.logo)}
                  alt="Logo"
                  className="h-8 object-contain"
                  loading="lazy"
                />
              ) : (
                <span className="font-heading text-xl font-bold text-primary">Inclusia</span>
              )}
            </div>
            <p className="text-gray-300 mb-4">
              {t("footer.aboutDesc")}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/company/110432609/admin/dashboard/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/inclusiapro/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/inclusiapro/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@inclusiapro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 2 - For Professionals */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-4 text-primary">
              {t("footer.forProfessionalsTitle")}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/how-it-works" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.howItWorks")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/pricing" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.pricing")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/become-professional" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.becomeVerified")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3 - Support */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-4 text-primary">
              {t("footer.supportTitle")}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/contact" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.contact")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/faq" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.faq")}
                </Link>
              </li>
              {showPartnersLink && (
                <li>
                  <Link 
                    to="/partners" 
                    className="text-gray-300 hover:text-primary transition-colors"
                  >
                    {t("footer.partners", "Partners")}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Column 4 - Legal */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-4 text-primary">
              {t("footer.legalTitle")}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/privacy" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.terms")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/cookie-policy" 
                  className="text-gray-300 hover:text-primary transition-colors"
                >
                  {t("footer.cookie")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter - hidden on homepage to avoid duplicate */}
        {!isHomePage && (
          <div className="pt-8 pb-4">
            <NewsletterForm variant="full" />
          </div>
        )}

        {/* Copyright */}
        <div className="pt-8 text-center text-gray-400">
          <p className="flex items-center justify-center gap-2 flex-wrap">
            © {currentYear} Inclusia - {t("footer.allRightsReserved")} •{" "}
            {t("footer.madeWith")} <Heart className="w-4 h-4 text-primary fill-primary" /> {t("footer.in")} •{" "}
            <button
              onClick={() => window.dispatchEvent(new Event('openCookieSettings'))}
              className="underline hover:no-underline hover:text-primary transition-colors"
            >
              {t("footer.manageCookies")}
            </button>
          </p>
        </div>
      </div>
    </footer>
  );
};
